/**
 * Database service - PouchDB wrapper for check-ins and app state
 */

import PouchDB from 'pouchdb-browser';
import PouchDBFind from 'pouchdb-find';
import { v4 as uuidv4 } from 'uuid';
import type { CheckIn, AppState, CreateCheckInData, AttachmentRef } from '../models/check-in.js';
import { getCurrentPeriod, getNextFullPeriod, type Period } from '../utils/time.js';

// Enable PouchDB Find plugin
PouchDB.plugin(PouchDBFind);

class DatabaseService {
  private db: PouchDB.Database;
  private initialized: boolean = false;

  constructor(dbName: string = 'full-life-pomodoro') {
    this.db = new PouchDB(dbName);
  }

  /**
   * Initialize database indexes
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Index for querying check-ins by time
      await this.db.createIndex({
        index: { fields: ['type', 'checkInTime'] }
      });

      // Index for duplicate detection
      await this.db.createIndex({
        index: { fields: ['type', 'currentPeriod.start'] }
      });

      console.log('Database initialized successfully');
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize database:', error);
      throw error;
    }
  }

  /**
   * Create a new check-in document
   */
  async createCheckIn(data: CreateCheckInData, lastCheckInTime: number | null, planCurrentPeriod: boolean = false): Promise<CheckIn> {
    const now = new Date();
    const currentPeriod = getCurrentPeriod(now);

    // Determine which period to plan
    let nextPlannedPeriod: Period;
    if (planCurrentPeriod && currentPeriod.type === 'activity') {
      // Plan current period ONLY if it's an activity period
      nextPlannedPeriod = currentPeriod;
    } else {
      // Otherwise, plan next activity period (getNextFullPeriod always returns activity)
      nextPlannedPeriod = getNextFullPeriod(now);
    }

    const checkIn: CheckIn = {
      _id: uuidv4(),
      type: 'check-in',
      checkInTime: now.getTime(),
      lastCheckInTime,
      currentPeriod: {
        start: currentPeriod.start.getTime(),
        end: currentPeriod.end.getTime(),
        type: currentPeriod.type
      },
      nextPlannedPeriod: {
        start: nextPlannedPeriod.start.getTime(),
        end: nextPlannedPeriod.end.getTime(),
        type: nextPlannedPeriod.type
      },
      pastDescription: data.pastDescription,
      futureDescription: data.futureDescription,
      attachmentKeys: {},
      createdAt: now.getTime(),
      updatedAt: now.getTime(),
      synced: false
    };

    console.log('createCheckIn - creating new check-in:', new Date(checkIn.checkInTime).toLocaleString());
    console.log('createCheckIn - pastDescription:', data.pastDescription.substring(0, 50));

    try {
      // Collect all attachment refs
      const attachments: AttachmentRef[] = [];

      // Save the document first
      const response = await this.db.put(checkIn as any);
      checkIn._rev = response.rev;

      // Process past media
      if (data.pastMedia) {
        for (let i = 0; i < data.pastMedia.length; i++) {
          const item = data.pastMedia[i];
          const key = `past-${item.mediaType}-${i}`;
          await this.addAttachment(checkIn._id, key, item.blob, item.contentType);
          attachments.push({ key, mediaType: item.mediaType, step: 'past', contentType: item.contentType });

          // Legacy compat: populate attachmentKeys for first of each type
          if (item.mediaType === 'photo' && !checkIn.attachmentKeys.pastPhoto) {
            checkIn.attachmentKeys.pastPhoto = key;
          } else if (item.mediaType === 'audio' && !checkIn.attachmentKeys.pastAudio) {
            checkIn.attachmentKeys.pastAudio = key;
          }
        }
      }

      // Process future media
      if (data.futureMedia) {
        for (let i = 0; i < data.futureMedia.length; i++) {
          const item = data.futureMedia[i];
          const key = `future-${item.mediaType}-${i}`;
          await this.addAttachment(checkIn._id, key, item.blob, item.contentType);
          attachments.push({ key, mediaType: item.mediaType, step: 'future', contentType: item.contentType });

          // Legacy compat: populate attachmentKeys for first of each type
          if (item.mediaType === 'photo' && !checkIn.attachmentKeys.futurePhoto) {
            checkIn.attachmentKeys.futurePhoto = key;
          } else if (item.mediaType === 'audio' && !checkIn.attachmentKeys.futureAudio) {
            checkIn.attachmentKeys.futureAudio = key;
          }
        }
      }

      // Update document with attachment metadata
      if (attachments.length > 0) {
        const doc = await this.db.get(checkIn._id) as CheckIn;
        doc.attachmentKeys = checkIn.attachmentKeys;
        doc.attachments = attachments;
        doc.updatedAt = Date.now();
        await this.db.put(doc as any);
      }

      return checkIn;
    } catch (error) {
      console.error('Failed to create check-in:', error);
      throw error;
    }
  }

  /**
   * Add an attachment to a document
   */
  async addAttachment(
    docId: string,
    attachmentName: string,
    blob: Blob,
    contentType: string
  ): Promise<void> {
    try {
      const doc = await this.db.get(docId);
      await this.db.putAttachment(docId, attachmentName, doc._rev as string, blob, contentType);
    } catch (error) {
      console.error('Failed to add attachment:', error);
      throw error;
    }
  }

  /**
   * Get an attachment from a document
   */
  async getAttachment(docId: string, attachmentName: string): Promise<Blob | null> {
    try {
      const blob = await this.db.getAttachment(docId, attachmentName);
      return blob as Blob;
    } catch (error) {
      if ((error as any).status === 404) {
        return null;
      }
      console.error('Failed to get attachment:', error);
      throw error;
    }
  }

  /**
   * Get recent check-ins with pagination
   */
  async getRecentCheckIns(limit: number = 50, skip: number = 0): Promise<CheckIn[]> {
    try {
      const result = await this.db.find({
        selector: {
          type: 'check-in',
          checkInTime: { $gt: 0 }
        },
        sort: [{ type: 'desc' }, { checkInTime: 'desc' }],
        limit,
        skip
      });

      return result.docs as CheckIn[];
    } catch (error) {
      console.error('Failed to get recent check-ins:', error);
      return [];
    }
  }

  /**
   * Get the most recent check-in
   */
  async getLastCheckIn(): Promise<CheckIn | null> {
    try {
      const result = await this.db.find({
        selector: {
          type: 'check-in',
          checkInTime: { $gt: 0 }
        },
        sort: [{ type: 'desc' }, { checkInTime: 'desc' }],
        limit: 1
      });

      return (result.docs[0] as CheckIn) || null;
    } catch (error) {
      console.error('Failed to get last check-in:', error);
      return null;
    }
  }

  /**
   * Check if a period has already been logged
   */
  async hasLoggedPeriod(periodStart: number): Promise<boolean> {
    try {
      const result = await this.db.find({
        selector: {
          type: 'check-in',
          'currentPeriod.start': periodStart
        },
        limit: 1
      });

      return result.docs.length > 0;
    } catch (error) {
      console.error('Failed to check for logged period:', error);
      return false;
    }
  }

  /**
   * Get or create the app state singleton
   */
  async getAppState(): Promise<AppState> {
    try {
      const doc = await this.db.get('app-state') as AppState;

      // Migrate old state: add soundVolume if missing
      if (doc.settings && doc.settings.soundVolume === undefined) {
        doc.settings.soundVolume = 0.5;
        await this.db.put(doc as any);
      }

      return doc;
    } catch (error) {
      if ((error as any).status === 404) {
        // Create default app state
        const defaultState: AppState = {
          _id: 'app-state',
          lastCheckInTime: null,
          currentPeriodId: '',
          settings: {
            soundEnabled: true,
            soundVolume: 0.5,
            darkMode: false,
            syncUrl: null,
            notificationsEnabled: false
          },
          updatedAt: Date.now()
        };

        try {
          await this.db.put(defaultState);
          return defaultState;
        } catch (putError) {
          console.error('Failed to create default app state:', putError);
          throw putError;
        }
      }
      console.error('Failed to get app state:', error);
      throw error;
    }
  }

  /**
   * Update app state
   */
  async updateAppState(updates: Partial<Omit<AppState, '_id' | '_rev'>>): Promise<AppState> {
    try {
      const state = await this.getAppState();
      const updated: AppState = {
        ...state,
        ...updates,
        updatedAt: Date.now()
      };

      await this.db.put(updated);
      return updated;
    } catch (error) {
      console.error('Failed to update app state:', error);
      throw error;
    }
  }

  /**
   * Normalize attachment refs from both old fixed-key and new array formats
   */
  static getAttachmentRefs(checkIn: CheckIn): AttachmentRef[] {
    // Prefer new array format
    if (checkIn.attachments && checkIn.attachments.length > 0) {
      return checkIn.attachments;
    }

    // Fall back to legacy fixed-key format
    const refs: AttachmentRef[] = [];
    const keys = checkIn.attachmentKeys;
    if (keys.pastPhoto) {
      refs.push({ key: keys.pastPhoto, mediaType: 'photo', step: 'past', contentType: 'image/jpeg' });
    }
    if (keys.pastAudio) {
      refs.push({ key: keys.pastAudio, mediaType: 'audio', step: 'past', contentType: 'audio/webm' });
    }
    if (keys.futurePhoto) {
      refs.push({ key: keys.futurePhoto, mediaType: 'photo', step: 'future', contentType: 'image/jpeg' });
    }
    if (keys.futureAudio) {
      refs.push({ key: keys.futureAudio, mediaType: 'audio', step: 'future', contentType: 'audio/webm' });
    }
    return refs;
  }

  /**
   * Get the underlying PouchDB instance
   */
  getDB(): PouchDB.Database {
    return this.db;
  }

  /**
   * Destroy the database (for testing or clearing data)
   */
  async destroy(): Promise<void> {
    try {
      await this.db.destroy();
      this.initialized = false;
    } catch (error) {
      console.error('Failed to destroy database:', error);
      throw error;
    }
  }
}

// Export singleton instance and class (for static methods)
export const databaseService = new DatabaseService();
export { DatabaseService };
export default databaseService;
